// URL-scheme allowlists for user- and provider-supplied URLs that end up as a
// live `href`. `z.string().url()` alone is NOT sufficient: `new URL()` accepts
// `javascript:`, `data:`, `vbscript:` and every other absolute scheme, so a
// stored value with one of those reaches the DOM (or an email) as a live
// `javascript:` link — stored XSS.
//
// Two sets, one owner:
//   - web URL  (http, https): fields that must point at a web page — event
//     join / registration links, member profile links, synced meeting links.
//   - link href (+ mailto, tel): anchors that may also open a mail client or
//     dialer — rendered external links and broadcast-email links.
//
// The WHATWG parser lowercases the scheme and strips leading/trailing
// whitespace and embedded tabs/newlines before parsing, so obfuscations like
// `JavaScript:`, ` javascript:` and `java\tscript:` resolve to their real
// scheme and are refused.
import { z } from 'zod';
const WEB_URL_SCHEMES = new Set(['http:', 'https:']);
const LINK_HREF_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);
export const WEB_URL_SCHEME_MESSAGE = 'URL must use the http:// or https:// scheme';
// The scheme `raw` resolves to (against `base` for relative input), or null
// when it does not parse as a URL at all.
function schemeOf(raw, base) {
    try {
        return new URL(raw, base).protocol;
    }
    catch {
        return null;
    }
}
// True only for an absolute http(s) URL.
export function hasWebUrlScheme(raw) {
    const scheme = schemeOf(raw);
    return scheme !== null && WEB_URL_SCHEMES.has(scheme);
}
// True when `raw` (resolved against `base` if relative) is safe to render as
// a clickable link: http(s), mailto or tel.
export function hasLinkHrefScheme(raw, base) {
    const scheme = schemeOf(raw, base);
    return scheme !== null && LINK_HREF_SCHEMES.has(scheme);
}
// Request-boundary schema for a stored web URL. `.url()` keeps the specific
// "not a URL" error for garbage input; the refine rejects non-web schemes.
export function webUrlSchema(maxLength) {
    return z
        .string()
        .url()
        .max(maxLength)
        .refine(hasWebUrlScheme, { message: WEB_URL_SCHEME_MESSAGE });
}
//# sourceMappingURL=web-scheme.js.map